import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  count?: number;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

export function MultiSelectFilter({ label, options, selected, onChange }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (value: string) => {
    const next = new Set(selected);
    next.has(value) ? next.delete(value) : next.add(value);
    onChange(next);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(new Set());
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-9 min-w-[120px] justify-between text-sm", selected.size > 0 && "border-primary")}>
          <span className="truncate max-w-[120px]">
            {selected.size === 0 ? label : selected.size === 1 ? (options.find(o => o.value === [...selected][0])?.label ?? [...selected][0]).slice(0, 16) : `${selected.size} 個已選`}
          </span>
          <div className="flex items-center gap-1 ml-1">
            {selected.size > 0 && (
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" onClick={clearAll} />
            )}
            <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`搜尋${label}...`} value={search} onValueChange={setSearch} />
          <CommandEmpty>無結果</CommandEmpty>
          <CommandGroup className="max-h-[200px] overflow-y-auto">
            {filteredOptions.map(option => (
              <CommandItem key={option.value} onSelect={() => toggle(option.value)} className="cursor-pointer">
                <Check className={cn("mr-2 h-4 w-4", selected.has(option.value) ? "opacity-100" : "opacity-0")} />
                <span className="flex-1 truncate">{option.label}</span>
                {option.count != null && <span className="text-xs text-muted-foreground ml-1">({option.count})</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
